/**
 * radarPalette.ts — قارئ تدرّج ألوان الرادار.
 *
 * المصدر الوحيد للألوان هو `radarPalette.json`، ويقرأه طرفان: هذا الملفّ (لمفتاح
 * الخريطة في الواجهة) و`backend/scripts/mrms_to_raster.py` (لتلوين النسيج نفسه).
 * ولا ثالث لهما عمداً — أي انحراف بين لون البكسل على الخريطة ولون نفس القيمة في
 * المفتاح عطبٌ لا يكتشفه أحد إلا بالعين، وأصله دائماً جدولان بدل واحد.
 *
 * المفتاح **dBZ** (انعكاسية الرادار) لا مم/ساعة: هذا ما يخرج من MRMS مباشرةً،
 * وتحويله عبر علاقة Z–R ثم تلوينه يُدخل خطأً تقديرياً بلا فائدة — والتدرّج نفسه
 * مأخوذ من عميل Windy الحيّ وهو مفهرس بـdBZ أصلاً.
 */

import palette from './radarPalette.json';
import { getLayerOverride } from './remoteConfig';

type Stop = [number, [number, number, number, number]];

const BUILTIN = palette.stops as Stop[];

/**
 * المحطّات الفعّالة — **من لوحة التحكّم إن وُجدت، وإلّا المخبوزة**.
 *
 * ولمَ لا تُقرأ مرّةً في متغيّر ثابت؟ لأن `hydrateRemoteConfig()` تصل بعد أوّل رسم،
 * فقيمةٌ تُلتقط عند تحميل الوحدة تبقى المخبوزة إلى الأبد ولا يرى المشرف أثر تعديله.
 * والدالّة رخيصة (قراءة مرجع)، وتُستدعى مرّة لكل رسم مفتاح لا لكل بكسل.
 *
 * والأهمّ أن هذا يُبقي **المفتاح والصورة على مصدر واحد**: المولّد
 * (`mrms_to_raster.py`) يقرأ نفس `color_stops` من نفس الملفّ. وأي انحراف بين لون
 * البكسل ولون نفس القيمة في المفتاح عطبٌ لا يكتشفه أحد إلا بالعين.
 */
function stops(): Stop[] {
    const remote = getLayerOverride('radar')?.color_stops;
    return remote && remote.length >= 2 ? (remote as Stop[]) : BUILTIN;
}

/** أدنى وأقصى dBZ في التدرّج. */
export const RADAR_DBZ_MIN = BUILTIN[0][0];
export const RADAR_DBZ_MAX = BUILTIN[BUILTIN.length - 1][0];

/** وحدة القياس كما يعلنها ملفّ المصدر. */
export const RADAR_UNIT = palette.unit as string;

/**
 * لون قيمة dBZ باستيفاء خطّي في RGBA بين المحطّتين المحيطتين.
 *
 * الاستيفاء في RGBA مباشرةً — لا sRGB خطّي ولا HCL — لأن هذا **بالضبط** ما يفعله
 * عميل Windy الذي استُخرج منه التدرّج (تحقّق مطابقة موثّق عند dBZ=14 في ملفّ اللوحة).
 * أي «تحسين» هنا يُنتج ألواناً أصحّ نظرياً وأبعد عن المرجع عملياً، ويكسر تطابق
 * المفتاح مع النسيج الذي يلوّنه المولّد بنفس القاعدة.
 */
export function radarColor(dbz: number): string {
    if (!Number.isFinite(dbz)) return 'rgba(0,0,0,0)';
    const S = stops();
    const v = Math.max(S[0][0], Math.min(S[S.length - 1][0], dbz));
    let i = 0;
    while (i < S.length - 2 && S[i + 1][0] < v) i += 1;
    const [v0, c0] = S[i];
    const [v1, c1] = S[i + 1];
    const t = v1 === v0 ? 0 : (v - v0) / (v1 - v0);
    const ch = (a: number, b: number) => Math.round(a + (b - a) * t);
    const alpha = c0[3] + (c1[3] - c0[3]) * t;
    return `rgba(${ch(c0[0], c1[0])}, ${ch(c0[1], c1[1])}, ${ch(c0[2], c1[2])}, ${alpha.toFixed(3)})`;
}

/**
 * محطّات المفتاح المعروضة للمستخدم.
 *
 * ليست كل محطّات التدرّج، بل الأرقام التي تعني شيئاً للقارئ: 10 رذاذ، 30 مطر
 * معتدل، 50 عاصفة رعدية، **70 بَرَد**. وهي حدود نطاقات سلّم NWS نفسها لا أرقاماً
 * وسطى، فيقع كل رقم على **حافّة لونٍ** ويقرأ المشاهد النطاق مباشرةً.
 *
 * وامتدادها إلى 70 لا 65 مقصود: الورديّ هو ما يميّز البَرَد، ومفتاحٌ ينتهي عند 65
 * يترك أهمّ لونٍ في المقياس خارج الشريط — فيرى المستخدم على الخريطة لوناً لا يجد
 * له معنى في المفتاح.
 */
export const RADAR_LEGEND_LABELS = [10, 20, 30, 40, 50, 60, 70];

/**
 * تدرّج CSS للمفتاح — **بمحور متساوي المسافات بين محطّات التسمية**، لا خطّي في dBZ.
 *
 * السبب أن الجزء المهمّ من المقياس (‏35–65 dBZ: من المطر الغزير إلى البَرَد) يقع في
 * أعلى النطاق، ومحورٌ خطّي يمنحه نصف الشريط بينما يُهدر النصف الآخر على فروق لا
 * تكاد تُرى. وهذا نفس ما يفعله شريط الرياح في هذا المشروع (‏gradientForBreakpoints).
 * الألوان داخل كل قطعة تبقى الاستيفاء الحقيقي، فلا يُفقد أي تفصيل لوني.
 */
export function radarGradientCss(stepsPerSegment = 10): string {
    const parts: string[] = [];
    const segments = RADAR_LEGEND_LABELS.length - 1;
    for (let seg = 0; seg < segments; seg += 1) {
        const a = RADAR_LEGEND_LABELS[seg];
        const b = RADAR_LEGEND_LABELS[seg + 1];
        for (let i = seg === 0 ? 0 : 1; i <= stepsPerSegment; i += 1) {
            const t = i / stepsPerSegment;
            const pos = ((seg + t) / segments) * 100;
            parts.push(`${radarColor(a + (b - a) * t)} ${pos.toFixed(2)}%`);
        }
    }
    return `linear-gradient(to right, ${parts.join(', ')})`;
}

/**
 * سلّم `color-relief` لبلاطات القيم: نفس محطّات `radarColor`، وما تحت `minDbz`
 * شفّاف — كما يفعل `colorize` في المولّد (‏`flat >= MIN_DBZ`).
 *
 * والفرق عن بلاطات الألوان أن الاستيفاء يقع **قبل** التلوين لكل بكسل شاشة: عند
 * التقريب فوق z7 تبقى الحافّة خطّاً ناعماً بألوان السلّم، لا بقعةً ممزوجة الألوان.
 * وهي طريقة Windy.
 *
 * ── لماذا تُكمَّم المحطّات ─────────────────────────────────────────────────────
 * MapLibre يخزّن قيم المحطّات **بترميز البلاطة نفسه** (‏`packDEMData`) ثم يفكّها في
 * الشيدر. فمحطّة لا تقع على خطوة الترميز (نصف dBZ) تنزل إلى ما دونها، ومحطّتان في
 * الخطوة نفسها تصيران متساويتين فيقسم الشيدر على صفر. فنضعها نحن على الخطوات، مع
 * إزاحةٍ صغيرة فوق الخطوة لأن الترميز يُقرّب إلى الأدنى (`floor`).
 */
export function radarReliefColor(minDbz: number, enc: { base: number; step: number }): unknown[] {
    const q = (d: number) => Math.round(d / enc.step) * enc.step + enc.step * 0.02;
    const expr: unknown[] = ['interpolate', ['linear'], ['elevation'],
        q(enc.base), 'rgba(0,0,0,0)', q(minDbz - enc.step), 'rgba(0,0,0,0)', q(minDbz), radarColor(minDbz)];
    let last = q(minDbz);
    for (const [d, c] of stops()) {
        const v = q(d);
        if (v <= last) continue;
        expr.push(v, `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${c[3]})`);
        last = v;
    }
    return expr;
}
